import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  UploadTaskSnapshot,
} from 'firebase/storage';
import { storage } from '../utils/firebaseSetUp';
import { isSelfHosted } from '../config/infra';
import { authenticatedHttpClient } from './httpClient';

/**
 * Maximum allowed product image size in bytes (2 MB by default)
 */
export const MAX_PRODUCT_IMAGE_BYTES = 2 * 1024 * 1024;

/**
 * Maximum allowed client image size in bytes (2 MB by default)
 */
export const MAX_CLIENT_IMAGE_BYTES = 2 * 1024 * 1024;

/**
 * Maximum allowed branding logo size in bytes (512 KB by default)
 */
export const MAX_BRANDING_LOGO_BYTES = 512 * 1024;

/**
 * Validate a product image file by size and type
 * Returns an error message string if invalid, otherwise null
 */
export function validateProductImageFile(
  file: File,
  options?: { maxBytes?: number; allowedMimeTypes?: string[] }
): string | null {
  const maxBytes = options?.maxBytes ?? MAX_PRODUCT_IMAGE_BYTES;
  const allowed = options?.allowedMimeTypes ?? [
    'image/jpeg',
    'image/jpg',
    'image/png',
  ];

  if (file.size > maxBytes) {
    return `La imagen es demasiado grande. Máximo permitido: ${formatBytes(
      maxBytes
    )}.`;
  }
  if (!allowed.includes(file.type)) {
    return 'Tipo de archivo no permitido. Use JPG o PNG.';
  }
  return null;
}

/**
 * Validate a client image file by size and type
 * Returns an error message string if invalid, otherwise null
 */
export function validateClientImageFile(
  file: File,
  options?: { maxBytes?: number; allowedMimeTypes?: string[] }
): string | null {
  const maxBytes = options?.maxBytes ?? MAX_CLIENT_IMAGE_BYTES;
  const allowed = options?.allowedMimeTypes ?? [
    'image/jpeg',
    'image/jpg',
    'image/png',
  ];

  if (file.size > maxBytes) {
    return `La imagen es demasiado grande. Máximo permitido: ${formatBytes(
      maxBytes
    )}.`;
  }
  if (!allowed.includes(file.type)) {
    return 'Tipo de archivo no permitido. Use JPG o PNG.';
  }
  return null;
}

/**
 * Validate a branding logo file by size and type
 * Returns an error message string if invalid, otherwise null
 */
export function validateBrandingLogoFile(
  file: File,
  options?: { maxBytes?: number; allowedMimeTypes?: string[] }
): string | null {
  const maxBytes = options?.maxBytes ?? MAX_BRANDING_LOGO_BYTES;
  const allowed = options?.allowedMimeTypes ?? [
    'image/png',
    'image/svg+xml',
    'image/jpeg',
    'image/jpg',
  ];

  if (file.size > maxBytes) {
    return `El logo es demasiado grande. Máximo permitido: ${formatBytes(
      maxBytes
    )}.`;
  }
  if (!allowed.includes(file.type)) {
    return 'Tipo de archivo no permitido. Use PNG, SVG o JPG.';
  }
  return null;
}

/**
 * Format bytes for UI messages
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const value = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
  return `${value} ${sizes[i]}`;
}

/**
 * Upload progress callback type
 */
export type UploadProgressCallback = (progress: number) => void;

/**
 * Upload error callback type
 */
export type UploadErrorCallback = (error: Error) => void;

/**
 * Upload complete callback type
 */
export type UploadCompleteCallback = (
  downloadURL: string,
  storagePath: string
) => void;

interface PresignedUploadResponse {
  url: string;
  path: string;
  publicUrl?: string;
}

interface ApiUploadResponse {
  path: string;
  publicUrl: string;
}

/**
 * Upload a file through the API (selfhosted / MinIO) — avoids browser CORS to MinIO.
 */
const uploadViaApi = (
  path: string,
  imageFile: File,
  onProgress?: UploadProgressCallback,
  onError?: UploadErrorCallback,
  onComplete?: UploadCompleteCallback
): void => {
  void (async () => {
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      const response =
        await authenticatedHttpClient.getAxiosInstance().post<ApiUploadResponse>(
          `/storage/upload?path=${encodeURIComponent(path)}`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: progressEvent => {
              if (progressEvent.total && onProgress) {
                onProgress((progressEvent.loaded / progressEvent.total) * 100);
              }
            },
          }
        );
      const { publicUrl, path: storagePath } = response.data;
      onComplete?.(publicUrl, storagePath);
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error('Upload failed'));
    }
  })();
};

/**
 * Upload a file via presigned PUT URL (selfhosted / MinIO direct).
 */
const uploadViaPresignedUrl = (
  path: string,
  imageFile: File,
  onProgress?: UploadProgressCallback,
  onError?: UploadErrorCallback,
  onComplete?: UploadCompleteCallback
): void => {
  void (async () => {
    try {
      const response = await authenticatedHttpClient.post<PresignedUploadResponse>(
        '/storage/presigned-upload-url',
        { path, content_type: imageFile.type || 'application/octet-stream' }
      );
      const { url, path: storagePath, publicUrl } = response;
      const contentType = imageFile.type || 'application/octet-stream';

      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', contentType);

      xhr.upload.onprogress = event => {
        if (event.lengthComputable && onProgress) {
          onProgress((event.loaded / event.total) * 100);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const downloadURL = publicUrl ?? storagePath;
          onComplete?.(downloadURL, storagePath);
        } else {
          onError?.(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        onError?.(new Error('Upload failed'));
      };

      xhr.send(imageFile);
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error('Upload failed'));
    }
  })();
};

/**
 * Delete image from storage using its storage path
 */
export const deleteImageByUrl = async (storagePath: string): Promise<void> => {
  if (isSelfHosted) {
    await authenticatedHttpClient.delete('/storage', {
      params: { path: storagePath },
    });
    return;
  }
  const imageRef = ref(storage, storagePath);
  await deleteObject(imageRef);
};

/**
 * Upload product image to Firebase Storage
 */
export const uploadProductImage = (
  organizationId: string,
  productId: string,
  imageFile: File,
  onProgress?: UploadProgressCallback,
  onError?: UploadErrorCallback,
  onComplete?: UploadCompleteCallback
): void => {
  const timestamp = Date.now();
  const fileName = `${timestamp}_${imageFile.name}`;
  const path = `organizations/${organizationId}/products/${productId}/${fileName}`;

  if (isSelfHosted) {
    uploadViaApi(path, imageFile, onProgress, onError, onComplete);
    return;
  }

  console.log('OrgID ', organizationId);

  // Create storage reference under organization scope
  const storageRef = ref(storage, path);

  // Create upload task
  const uploadTask = uploadBytesResumable(storageRef, imageFile);

  // Handle upload events
  uploadTask.on(
    'state_changed',
    (snapshot: UploadTaskSnapshot) => {
      // Calculate progress
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

      console.log(`Upload is ${progress}% done`);

      // Call progress callback if provided
      if (onProgress) {
        onProgress(progress);
      }

      // Log state changes
      switch (snapshot.state) {
        case 'paused':
          console.log('Upload is paused');
          break;
        case 'running':
          console.log('Upload is running');
          break;
      }
    },
    (error: Error) => {
      // Handle upload error
      console.error('Upload error:', error);
      if (onError) {
        onError(error);
      }
    },
    async () => {
      // Handle successful upload
      try {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        console.log('File available at:', downloadURL);

        if (onComplete) {
          onComplete(downloadURL, path);
        }
      } catch (error) {
        console.error('Error getting download URL:', error);
        if (onError) {
          onError(error as Error);
        }
      }
    }
  );
};

/**
 * Upload client image to Firebase Storage
 */
export const uploadClientImage = (
  organizationId: string,
  clientId: string,
  imageFile: File,
  onProgress?: UploadProgressCallback,
  onError?: UploadErrorCallback,
  onComplete?: UploadCompleteCallback
): void => {
  const timestamp = Date.now();
  const fileName = `${timestamp}_${imageFile.name}`;
  const path = `organizations/${organizationId}/clients/${clientId}/${fileName}`;

  if (isSelfHosted) {
    uploadViaApi(path, imageFile, onProgress, onError, onComplete);
    return;
  }

  console.log('OrgID ', organizationId);

  const storageRef = ref(storage, path);

  // Create upload task
  const uploadTask = uploadBytesResumable(storageRef, imageFile);

  // Handle upload events
  uploadTask.on(
    'state_changed',
    (snapshot: UploadTaskSnapshot) => {
      // Calculate progress
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

      console.log(`Upload is ${progress}% done`);

      // Call progress callback if provided
      if (onProgress) {
        onProgress(progress);
      }

      // Log state changes
      switch (snapshot.state) {
        case 'paused':
          console.log('Upload is paused');
          break;
        case 'running':
          console.log('Upload is running');
          break;
      }
    },
    (error: Error) => {
      // Handle upload error
      console.error('Upload error:', error);
      if (onError) {
        onError(error);
      }
    },
    async () => {
      // Handle successful upload
      try {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        console.log('File available at:', downloadURL);

        if (onComplete) {
          onComplete(downloadURL, path);
        }
      } catch (error) {
        console.error('Error getting download URL:', error);
        if (onError) {
          onError(error as Error);
        }
      }
    }
  );
};

/**
 * Get storage path for product image
 */
export const getProductImageStoragePath = (
  organizationId: string,
  productId: string,
  fileName: string
): string => {
  return `organizations/${organizationId}/products/${productId}/${fileName}`;
};

/**
 * Get storage path for client image
 */
export const getClientImageStoragePath = (
  organizationId: string,
  clientId: string,
  fileName: string
): string => {
  return `organizations/${organizationId}/clients/${clientId}/${fileName}`;
};

/**
 * Upload branding logo to Firebase Storage
 */
export const uploadBrandingLogo = (
  organizationId: string,
  imageFile: File,
  onProgress?: UploadProgressCallback,
  onError?: UploadErrorCallback,
  onComplete?: UploadCompleteCallback
): void => {
  const timestamp = Date.now();
  const fileName = `${timestamp}_${imageFile.name}`;
  const path = `organizations/${organizationId}/branding/logo/${fileName}`;

  if (isSelfHosted) {
    uploadViaApi(path, imageFile, onProgress, onError, onComplete);
    return;
  }

  const storageRef = ref(storage, path);

  const uploadTask = uploadBytesResumable(storageRef, imageFile);

  uploadTask.on(
    'state_changed',
    (snapshot: UploadTaskSnapshot) => {
      const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
      if (onProgress) onProgress(progress);
    },
    (error: Error) => {
      if (onError) onError(error);
    },
    async () => {
      try {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        if (onComplete) onComplete(downloadURL, path);
      } catch (error) {
        if (onError) onError(error as Error);
      }
    }
  );
};
